/**
 * Define — production page-family (WP1c / #46599, WP6f / #46630, WP6g / #46631).
 *
 * Workflow + intake pages live in workflows.tsx (WP6e / #46629).
 * WP6f fills Controls list/detail. WP6g fills Policies list/detail: request
 * activation per HG11 (`policy:approve` on Real), data-action-id bindings.
 * No styleguide-only toasts. Demo and Real use WP4b
 * controls/policies contracts plus WP4a runsCreate.
 */

import { useMemo, useState, type ReactNode } from "react";
import { formatNumber, formatRelativeTime } from "@excellent/ui";
import { apiCall, useApi, ApiError } from "@excellent/ui/api";
import { universalVerification } from "@excellent/ui/api/contracts/universal-verification";
import { projectUniversalCompletionOverview } from "@excellent/core/verification-universal-surfaces-client";
import type {
  VerificationControlDto,
  VerificationPolicyDto,
} from "@excellent/ui/api/contracts/verification-define";
import { stripEnvironmentPrefix, type VerificationEnvironment } from "../env";
import { useVerificationEnvOptional } from "../env-provider";
import { href } from "../nav";
import {
  ActionButton,
  ButtonRow,
  Callout,
  Card,
  CardContent,
  DataTable,
  displayLabel,
  Field,
  Fields,
  FilterField,
  FilterToolbar,
  ListRow,
  ListSearch,
  Mono,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Section,
  Stat,
  StatGrid,
  Textarea,
  Severity,
  ToneBadge,
  Verdict,
  type DataTableColumn,
} from "../ui";
import {
  PageError,
  PageFrame,
  PageLink,
  PagePending,
  type VerificationDetailPageProps,
  type VerificationPageProps,
} from "./page-frame";
import { verificationCanonicalClients } from "../api/canonical-clients";
import { combineCompletionHistory, currentAuthorityIds } from "../completion-history";

export { WorkflowDetailPage, WorkflowNewPage, WorkflowsPage } from "./workflows";

export const CONTROLS_RUN_ALL_ACTION = "controls.run-all";
export const CONTROLS_SEARCH_ACTION = "controls.search";
export const CONTROLS_FILTER_FAMILY_ACTION = "controls.filter-family";
export const CONTROLS_OPEN_ROW_ACTION = "controls.open-row";
export const CONTROLS_BACK_ACTION = "controls.back";
export const CONTROLS_REVERIFY_ACTION = "controls.reverify";
export const CONTROLS_OPEN_POLICY_ACTION = "controls.open-policy";
export const CONTROLS_OPEN_FINDING_ACTION = "controls.open-finding";
export const CONTROLS_OPEN_CONNECTORS_ACTION = "controls.open-connectors";
export const POLICIES_OPEN_ROW_ACTION = "policies.open-row";
export const POLICIES_BACK_ACTION = "policies.back";
export const POLICIES_SEARCH_ACTION = "policies.search";
export const POLICIES_FILTER_STATUS_ACTION = "policies.filter-status";
export const POLICIES_FILTER_CONTROL_ACTION = "policies.filter-control";
export const POLICIES_RATIONALE_ACTION = "policies.rationale";
export const POLICIES_REQUEST_ACTIVATION_ACTION = "policies.request-activation";
export const POLICIES_OPEN_CONTROL_ACTION = "policies.open-control";
export const POLICIES_OPEN_VERSION_ACTION = "policies.open-version";

const LIST_LEDE =
  "Accepted contracts define what done means before work starts. Each version binds checks, evidence requirements, sources, bounds, and acceptance authority.";
const DETAIL_LEDE =
  "One requirement evaluated against evidence: owner, frequency, population, current verdict, and the policy that decides it.";
const POLICIES_LEDE =
  "The checkable logic behind each control, versioned. Tightening the bar is routine; loosening it is not — that path requires impact analysis against frozen cases and activation by a second person.";
const POLICY_DETAIL_LEDE =
  "One versioned, checkable logic that decides a control from its observations.";

function withDemoFallback(node: ReactNode): ReactNode {
  return <>{node}</>;
}

function MaybeDemo({ children }: { children: ReactNode }) {
  const existing = null;
  if (existing) return <>{children}</>;
  return withDemoFallback(children);
}

function errorMessage(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  if (err instanceof Error) return err.message;
  return "The action did not complete.";
}

function environmentActor(environment: VerificationEnvironment): string {
  return environment === "demo" ? "demo:act_mw" : "real:act_matt";
}

function consequenceTone(
  level: VerificationControlDto["consequence"],
): "fail" | "unknown" | "neutral" {
  if (level === "critical" || level === "high") return "fail";
  if (level === "medium") return "unknown";
  return "neutral";
}

function controlsColumns(
  environment: VerificationEnvironment,
): DataTableColumn<VerificationControlDto>[] {
  return [
    {
      key: "control",
      header: "Control",
      cellClassName: "whitespace-normal",
      render: (control) => (
        <div
          className="flex flex-col gap-0.5"
          data-control-id={control.id}
          data-demo-scoped={environment === "demo" ? "true" : undefined}
        >
          <span className="font-medium text-foreground">{control.title}</span>
          <Mono>{control.ref}</Mono>
          <span className="type-subtext text-muted-foreground">{displayLabel(control.family)}</span>
        </div>
      ),
    },
    {
      key: "verdict",
      header: "Verdict",
      render: (control) => <Verdict status={control.status} />,
    },
    {
      key: "consequence",
      header: "Consequence",
      render: (control) => (
        <ToneBadge tone={consequenceTone(control.consequence)}>{control.consequence}</ToneBadge>
      ),
    },
    {
      key: "findings",
      header: "Open findings",
      render: (control) =>
        control.openFindingCount === 0 ? (
          <span className="text-muted-foreground">—</span>
        ) : (
          <ToneBadge tone="fail">{formatNumber(control.openFindingCount)}</ToneBadge>
        ),
    },
    {
      key: "population",
      header: "Population",
      render: (control) => (
        <Mono>
          {control.sampledSize === control.populationSize
            ? `${formatNumber(control.populationSize)} (all)`
            : `${formatNumber(control.sampledSize)} / ${formatNumber(control.populationSize)}`}
        </Mono>
      ),
    },
    {
      key: "frequency",
      header: "Frequency",
      render: (control) => displayLabel(control.frequency),
    },
  ];
}

export function ControlsPage({ onNavigate }: VerificationPageProps) {
  return (
    <MaybeDemo>
      <ControlsPageInner onNavigate={onNavigate} />
    </MaybeDemo>
  );
}

function ControlsPageInner({ onNavigate }: VerificationPageProps) {
  const env = useVerificationEnvOptional();
  const environment: VerificationEnvironment | null = env?.environment ?? null;
  const ready = env?.ready ?? true;
  const [q, setQ] = useState("");
  const [family, setFamily] = useState("all");
  const [notice, setNotice] = useState<string | null>(null);
  const [pending, setPending] = useState<string | null>(null);

  const controlsApi = useApi(
    verificationCanonicalClients.controlsList.operation,
    environment && ready ? { query: { env: environment } } : null,
  );
  const universalApi = useApi(
    universalVerification.overview,
    environment === "real" && ready ? {} : null,
  );
  const completionProjection = useMemo(
    () => (universalApi.data ? projectUniversalCompletionOverview(universalApi.data) : null),
    [universalApi.data],
  );
  const readableContracts = useMemo(
    () =>
      completionProjection
        ? combineCompletionHistory(
            completionProjection.current.contracts,
            completionProjection.incomplete.contracts,
            (contract) => contract.contractDigest,
          )
        : [],
    [completionProjection],
  );
  const currentContractDigests = useMemo(
    () =>
      currentAuthorityIds(
        completionProjection?.current.contracts ?? [],
        (contract) => contract.contractDigest,
      ),
    [completionProjection],
  );
  const pendingCurrentAmendments = useMemo(() => {
    if (!completionProjection) return [];
    return completionProjection.current.amendments.filter(({ decision }) => decision === null);
  }, [completionProjection]);

  const catalog = useMemo(() => {
    return controlsApi.data?.items ?? [];
  }, [controlsApi.data]);

  const families = useMemo(
    () => [...new Set(catalog.map((control) => control.family))].sort(),
    [catalog],
  );

  const rows = useMemo(() => {
    let list = catalog;
    if (family !== "all") list = list.filter((control) => control.family === family);
    if (q.trim()) {
      const n = q.toLowerCase();
      list = list.filter(
        (control) =>
          control.title.toLowerCase().includes(n) ||
          control.ref.toLowerCase().includes(n) ||
          control.family.toLowerCase().includes(n),
      );
    }
    return list;
  }, [catalog, family, q]);

  const runAllDisabledReason =
    catalog.length === 0
      ? "No controls in this workspace yet."
      : !environment
        ? "Load the active Environment first."
        : null;

  async function onRunAll() {
    if (runAllDisabledReason) return;
    setPending(CONTROLS_RUN_ALL_ACTION);
    setNotice(null);
    try {
      if (!environment) return;
      const created = await apiCall(verificationCanonicalClients.runsCreate.operation, {
        query: { env: environment },
        body: {
          actorId: environmentActor(environment),
          label: "Full control sweep",
          controlIds: catalog.map((control) => control.id),
        },
      });
      setNotice(`Queued ${created.run.label}.`);
      onNavigate(href("runs", created.run.id));
    } catch (err) {
      setNotice(errorMessage(err));
    } finally {
      setPending(null);
    }
  }

  if (env && !ready) {
    return (
      <PageFrame page="controls" wide eyebrow="Define" title="Contracts" lede={LIST_LEDE}>
        <PagePending title="Loading environment">
          Loading the control library for the active Environment.
        </PagePending>
      </PageFrame>
    );
  }

  if (environment && controlsApi.isLoading && !controlsApi.data) {
    return (
      <PageFrame page="controls" wide eyebrow="Define" title="Contracts" lede={LIST_LEDE}>
        <PagePending title="Loading controls">Fetching controls for this environment.</PagePending>
      </PageFrame>
    );
  }

  if (environment && controlsApi.error && !controlsApi.data) {
    return (
      <PageFrame page="controls" wide eyebrow="Define" title="Contracts" lede={LIST_LEDE}>
        <PageError
          title="Controls are unavailable"
          error={controlsApi.error}
          onRetry={() => void controlsApi.mutate()}
        />
        <ButtonRow>
          <ActionButton
            actionId={CONTROLS_RUN_ALL_ACTION}
            data-action-id={CONTROLS_RUN_ALL_ACTION}
            disabled
          >
            Run all
          </ActionButton>
          <ActionButton
            actionId={CONTROLS_OPEN_ROW_ACTION}
            data-action-id={CONTROLS_OPEN_ROW_ACTION}
            disabled
          >
            Open control
          </ActionButton>
        </ButtonRow>
      </PageFrame>
    );
  }

  if (environment !== "demo" && environment !== "real") {
    return (
      <PageFrame page="controls" wide eyebrow="Define" title="Contracts" lede={LIST_LEDE}>
        <PagePending title="Environment unavailable">
          Controls are environment-scoped. Pick an environment to inspect its control library.
        </PagePending>
      </PageFrame>
    );
  }

  return (
    <PageFrame page="controls" wide eyebrow="Define" title="Contracts" lede={LIST_LEDE}>
      {environment === "demo" ? (
        <Callout tone="neutral" title="Demo control projection">
          Canonical contracts are isolated to Real. Demo continues to show its scoped operational
          controls below.
        </Callout>
      ) : universalApi.error ? (
        <Callout tone="unknown" title="Canonical contracts unavailable">
          No control records are being presented as accepted contracts. Retry the canonical ledger
          before relying on contract counts.
        </Callout>
      ) : universalApi.isLoading && !universalApi.data ? (
        <PagePending title="Loading accepted contracts">
          Reading immutable contract versions from the canonical Work ledger.
        </PagePending>
      ) : universalApi.data && completionProjection ? (
        <Section
          title="Completion-aware contracts"
          note={`Generated ${formatRelativeTime(universalApi.data.generatedAt)} from the canonical Work ledger. Only current completion-aware versions are authoritative.`}
        >
          {completionProjection.incomplete.contracts.length > 0 ? (
            <Callout tone="unknown" title="Historical contracts are incomplete">
              {completionProjection.incomplete.contracts.length} readable contract
              {completionProjection.incomplete.contracts.length === 1 ? " is" : "s are"} missing a
              current completion definition. Recorded terms remain inspectable below but cannot
              authorize an attempt or enforcement.
            </Callout>
          ) : null}
          <StatGrid cols={4}>
            <Stat
              label="Current contracts"
              value={completionProjection.current.contracts.length}
              foot="Completion-aware versions"
            />
            <Stat
              label="Current checks"
              value={completionProjection.current.contracts.reduce(
                (sum, contract) => sum + contract.content.obligations.length,
                0,
              )}
              foot="Bound obligations"
            />
            <Stat
              label="Current sources"
              value={
                new Set(
                  completionProjection.current.contracts.flatMap((contract) =>
                    contract.content.sources.map((source) => source.uri),
                  ),
                ).size
              }
              foot="Bound references"
            />
            <Stat
              label="Current amendments"
              value={pendingCurrentAmendments.length}
              foot="Awaiting acceptance on current Work and base contract"
            />
          </StatGrid>
          <DataTable
            rows={readableContracts}
            rowKey={(contract) => contract.contractDigest}
            emptyState={{
              title: "No accepted contracts",
              description:
                "Work can be requested, but execution should not begin until a requester, named human, or policy accepts the compiled contract.",
            }}
            columns={[
              {
                key: "contract",
                header: "Contract",
                render: (contract) => (
                  <div className="flex min-w-0 flex-col gap-1">
                    <span className="font-medium text-foreground">{contract.content.title}</span>
                    <Mono>
                      {contract.content.contractId} · v{contract.content.version}
                    </Mono>
                  </div>
                ),
              },
              {
                key: "checks",
                header: "Checks and evidence",
                cellClassName: "whitespace-normal",
                render: (contract) => (
                  <details>
                    <summary className="cursor-pointer font-medium text-foreground">
                      {contract.content.obligations.length} check
                      {contract.content.obligations.length === 1 ? "" : "s"}
                    </summary>
                    <div className="mt-2 flex min-w-[18rem] flex-col gap-2">
                      {contract.content.obligations.map((obligation) => (
                        <div key={obligation.id} className="flex flex-col gap-0.5">
                          <span>{obligation.predicate.description}</span>
                          <span className="type-subtext text-muted-foreground">
                            {obligation.predicate.kind} ·{" "}
                            {obligation.evidence
                              .map(
                                (requirement) =>
                                  `${requirement.kind} × ${requirement.minimumCount}`,
                              )
                              .join(", ")}
                          </span>
                        </div>
                      ))}
                    </div>
                  </details>
                ),
              },
              {
                key: "sources",
                header: "Sources",
                render: (contract) => contract.content.sources.length,
              },
              {
                key: "bounds",
                header: "Bounds",
                render: (contract) =>
                  `${contract.content.bounds.network} network · ${contract.content.bounds.allowedMutations.length} mutations`,
              },
              {
                key: "accepted",
                header: "Acceptance",
                render: (contract) =>
                  currentContractDigests.has(contract.contractDigest) ? (
                    `${contract.acceptance.acceptedBy.actorId} · ${formatRelativeTime(contract.acceptance.acceptedAt)}`
                  ) : (
                    <div className="flex flex-col gap-1">
                      <ToneBadge tone="unknown">INCOMPLETE</ToneBadge>
                      <span className="type-subtext text-muted-foreground">
                        Recorded {contract.acceptance.acceptedBy.actorId} ·{" "}
                        {formatRelativeTime(contract.acceptance.acceptedAt)}
                      </span>
                    </div>
                  ),
              },
              {
                key: "authority",
                header: "Authority",
                render: (contract) =>
                  currentContractDigests.has(contract.contractDigest) ? (
                    <ToneBadge tone="neutral">{contract.acceptance.authorityBasis}</ToneBadge>
                  ) : (
                    <span className="type-subtext text-muted-foreground">
                      Recorded {contract.acceptance.authorityBasis}
                    </span>
                  ),
              },
              {
                key: "mode",
                header: "Mode",
                render: (contract) =>
                  currentContractDigests.has(contract.contractDigest) ? (
                    <ToneBadge tone={contract.deploymentMode === "ENFORCED" ? "pass" : "neutral"}>
                      {contract.deploymentMode}
                    </ToneBadge>
                  ) : (
                    <span className="type-subtext text-muted-foreground">
                      Recorded {contract.deploymentMode}
                    </span>
                  ),
              },
            ]}
          />
        </Section>
      ) : null}
      {notice ? <Callout tone="neutral">{notice}</Callout> : null}
      <Section
        title="Operational control projection"
        note="Controls support compliance and scheduled sweeps. They are not substituted for accepted Work contracts."
        actions={
          <ActionButton
            actionId={CONTROLS_RUN_ALL_ACTION}
            variant="primary"
            disabled={Boolean(runAllDisabledReason) || pending === CONTROLS_RUN_ALL_ACTION}
            reason={runAllDisabledReason ?? undefined}
            onClick={() => void onRunAll()}
          >
            Run controls
          </ActionButton>
        }
      >
        <DataTable
          toolbar={
            <FilterToolbar>
              <FilterField label="Search" wide>
                <ListSearch
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search controls…"
                  aria-label="Search controls"
                  data-action-id={CONTROLS_SEARCH_ACTION}
                />
              </FilterField>
              <FilterField label="Family">
                <Select value={family} onValueChange={setFamily}>
                  <SelectTrigger
                    className="w-full"
                    data-action-id={CONTROLS_FILTER_FAMILY_ACTION}
                    aria-label="Filter by family"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All families</SelectItem>
                    {families.map((name) => (
                      <SelectItem key={name} value={name}>
                        {displayLabel(name)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FilterField>
            </FilterToolbar>
          }
          emptyState={{
            title: catalog.length === 0 ? "No controls in scope" : "No controls match",
            description: (
              <>
                {catalog.length === 0
                  ? "This workspace has no controls yet. Queue a full control sweep once controls exist."
                  : "Clear the filters to see the full library."}{" "}
                <PageLink onNavigate={onNavigate} to="policies">
                  Open policies
                </PageLink>
                .
              </>
            ),
          }}
          rows={rows}
          rowKey={(control) => control.id}
          rowId={(control) => `controls-row-${control.id}`}
          rowActionId={CONTROLS_OPEN_ROW_ACTION}
          onRowClick={(control) => onNavigate(href("controls", control.id))}
          columns={controlsColumns(environment)}
        />
      </Section>
    </PageFrame>
  );
}

export function ControlDetailPage({ id, onNavigate }: VerificationDetailPageProps) {
  return (
    <MaybeDemo>
      <ControlDetailPageInner id={id} onNavigate={onNavigate} />
    </MaybeDemo>
  );
}

function ControlDetailPageInner({ id, onNavigate }: VerificationDetailPageProps) {
  const env = useVerificationEnvOptional();
  const environment: VerificationEnvironment | null = env?.environment ?? null;
  const ready = env?.ready ?? true;
  const [notice, setNotice] = useState<string | null>(null);
  const [pending, setPending] = useState<string | null>(null);

  const controlApi = useApi(
    verificationCanonicalClients.controlsGet.operation,
    environment && ready ? { params: { id }, query: { env: environment } } : null,
  );

  const control = controlApi.data?.control ?? null;
  const reverifyDisabledReason =
    environment && control
      ? null
      : environment
        ? "No control loaded."
        : "Load the active Environment first.";

  async function onReverify() {
    if (reverifyDisabledReason || !control) return;
    setPending(CONTROLS_REVERIFY_ACTION);
    setNotice(null);
    try {
      if (!environment) return;
      const created = await apiCall(verificationCanonicalClients.runsCreate.operation, {
        query: { env: environment },
        body: {
          actorId: environmentActor(environment),
          label: `Re-verify ${control.ref}`,
          controlIds: [control.id],
        },
      });
      setNotice(`Queued ${created.run.label}.`);
      onNavigate(href("runs", created.run.id));
    } catch (err) {
      setNotice(errorMessage(err));
    } finally {
      setPending(null);
    }
  }

  if (env && !ready) {
    return (
      <PageFrame page="control" wide eyebrow="Define" title="Control" lede={DETAIL_LEDE}>
        <PagePending title="Loading environment">
          Loading this control for the active Environment.
        </PagePending>
      </PageFrame>
    );
  }

  if (environment && controlApi.isLoading && !controlApi.data) {
    return (
      <PageFrame page="control" wide eyebrow="Define" title="Control" lede={DETAIL_LEDE}>
        <PagePending title={`Loading control ${id}`}>Fetching the control contract.</PagePending>
      </PageFrame>
    );
  }

  if (environment && controlApi.error && !controlApi.data) {
    return (
      <PageFrame page="control" wide eyebrow="Define" title="Control" lede={DETAIL_LEDE}>
        <PageError
          title={`No control loaded for ${id}`}
          error={controlApi.error}
          onRetry={() => void controlApi.mutate()}
        />
        <ButtonRow>
          <ActionButton
            actionId={CONTROLS_REVERIFY_ACTION}
            data-action-id={CONTROLS_REVERIFY_ACTION}
            disabled
          >
            Re-verify
          </ActionButton>
          <ActionButton
            actionId={CONTROLS_OPEN_POLICY_ACTION}
            data-action-id={CONTROLS_OPEN_POLICY_ACTION}
            disabled
          >
            Open policy
          </ActionButton>
          <ActionButton
            actionId={CONTROLS_BACK_ACTION}
            data-action-id={CONTROLS_BACK_ACTION}
            onClick={() => onNavigate(href("controls"))}
          >
            Back to controls
          </ActionButton>
        </ButtonRow>
      </PageFrame>
    );
  }

  if (environment !== "demo" && environment !== "real") {
    return (
      <PageFrame page="control" wide eyebrow="Define" title="Control" lede={DETAIL_LEDE}>
        <PagePending title={`No control loaded for ${id}`}>
          Load the active Environment to open a control.{" "}
          <PageLink onNavigate={onNavigate} to="controls">
            Back to controls
          </PageLink>
          .
        </PagePending>
      </PageFrame>
    );
  }

  if (!control) {
    return (
      <PageFrame page="control" wide eyebrow="Define" title="Control" lede={DETAIL_LEDE}>
        <PagePending title={`No control loaded for ${id}`}>
          That id is not in this environment.{" "}
          <PageLink onNavigate={onNavigate} to="controls">
            Back to controls
          </PageLink>
          .
        </PagePending>
        <ButtonRow>
          <ActionButton
            actionId={CONTROLS_BACK_ACTION}
            data-action-id={CONTROLS_BACK_ACTION}
            onClick={() => onNavigate(href("controls"))}
          >
            Back to controls
          </ActionButton>
        </ButtonRow>
      </PageFrame>
    );
  }

  const sampledPct = Math.round((control.sampledSize / (control.populationSize || 1)) * 100);
  const sourceHealth = control.sourceIds.length === 0 ? "fail" : "unknown";

  return (
    <PageFrame
      page="control"
      wide
      eyebrow={`${control.framework} ${control.ref} · ${control.family}`}
      title={control.title}
      lede={`Owned by ${control.ownerActorId ? stripEnvironmentPrefix(control.ownerActorId) : "unassigned"}. Checked ${control.frequency}. Consequence ${control.consequence}.`}
      actions={
        <>
          <ActionButton
            actionId={CONTROLS_REVERIFY_ACTION}
            disabled={Boolean(reverifyDisabledReason) || pending === CONTROLS_REVERIFY_ACTION}
            reason={reverifyDisabledReason ?? undefined}
            onClick={() => void onReverify()}
          >
            Re-verify
          </ActionButton>
          {control.activePolicyId ? (
            <ActionButton
              actionId={CONTROLS_OPEN_POLICY_ACTION}
              variant="primary"
              onClick={() => {
                const policyId = control.activePolicyId;
                if (!policyId) return;
                onNavigate(href("policies", policyId));
              }}
            >
              Open policy
            </ActionButton>
          ) : (
            <ActionButton
              actionId={CONTROLS_OPEN_POLICY_ACTION}
              disabled
              reason="No active policy is bound to this control."
            >
              Open policy
            </ActionButton>
          )}
          <ActionButton
            actionId={CONTROLS_BACK_ACTION}
            onClick={() => onNavigate(href("controls"))}
          >
            Back to controls
          </ActionButton>
        </>
      }
    >
      {notice ? <Callout tone="neutral">{notice}</Callout> : null}

      <StatGrid cols={4}>
        <Stat label="Current verdict" value={<Verdict status={control.status} />} />
        <Stat
          label="Open findings"
          value={formatNumber(control.openFindingCount)}
          tone={control.openFindingCount > 0 ? "fail" : "pass"}
          foot={control.openFindingCount > 0 ? "Needs a decision" : "Nothing outstanding"}
        />
        <Stat
          label="Population tested"
          value={`${formatNumber(sampledPct)}%`}
          tone={control.sampledSize === control.populationSize ? "pass" : "unknown"}
          foot={`${formatNumber(control.sampledSize)} of ${formatNumber(control.populationSize)} items`}
        />
        <Stat
          label="Evidence sources"
          value={formatNumber(control.sourceIds.length)}
          tone={sourceHealth === "fail" ? "fail" : sourceHealth === "unknown" ? "unknown" : "pass"}
          foot={
            <>
              {control.sourceIds.length === 0
                ? "Nothing mapped — the control cannot run"
                : `${formatNumber(control.sourceIds.length)} mapped source${control.sourceIds.length === 1 ? "" : "s"}`}
              <ActionButton
                actionId={CONTROLS_OPEN_CONNECTORS_ACTION}
                data-action-id={CONTROLS_OPEN_CONNECTORS_ACTION}
                onClick={() => onNavigate(href("connectors"))}
              >
                Open connectors
              </ActionButton>
            </>
          }
        />
      </StatGrid>

      {control.status === "NOT_RUN" ? (
        <Callout tone="unknown" title="This control has never been verified.">
          No evidence source is mapped to it, so no check has ever run. It will appear in any pack
          as a scope gap — which is honest, but it is not evidence.
        </Callout>
      ) : null}

      {control.activePolicyId ? (
        <Card>
          <CardContent className="flex flex-col gap-3">
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div>
                <div className="type-body font-medium">Active policy</div>
                <div className="type-subtext text-muted-foreground">
                  {stripEnvironmentPrefix(control.activePolicyId)}
                </div>
              </div>
              <ActionButton
                actionId={CONTROLS_OPEN_POLICY_ACTION}
                variant="ghost"
                onClick={() =>
                  control.activePolicyId && onNavigate(href("policies", control.activePolicyId))
                }
              >
                Open
              </ActionButton>
            </div>
            <p className="type-subtext text-muted-foreground">
              Open the policy record to inspect its expression, replay cases, and activation
              history.
            </p>
          </CardContent>
        </Card>
      ) : null}
    </PageFrame>
  );
}

function policyControl(controlId: string) {
  return { ref: stripEnvironmentPrefix(controlId), title: stripEnvironmentPrefix(controlId) };
}

function actorName(id: string | null | undefined) {
  if (!id) return "—";
  return stripEnvironmentPrefix(id);
}

function policyStatusTone(policy: Pick<VerificationPolicyDto, "status" | "blockedReason">) {
  if (policy.status === "active") return "pass" as const;
  if (policy.blockedReason) return "fail" as const;
  return "neutral" as const;
}

function intentTone(intent: VerificationPolicyDto["intent"]) {
  if (intent === "loosen") return "unknown" as const;
  if (intent === "tighten") return "pass" as const;
  return "neutral" as const;
}

function policiesColumns(
  environment: VerificationEnvironment | null,
): DataTableColumn<VerificationPolicyDto>[] {
  return [
    {
      key: "control",
      header: "Control",
      cellClassName: "whitespace-normal",
      render: (policy) => {
        const control = policyControl(policy.controlId);
        return (
          <div
            className="flex flex-col gap-0.5"
            data-policy-id={policy.id}
            data-demo-scoped={environment === "demo" ? "true" : undefined}
          >
            <span className="font-medium text-foreground">{control?.title ?? policy.id}</span>
            <Mono>{control?.ref ?? stripEnvironmentPrefix(policy.controlId)}</Mono>
          </div>
        );
      },
    },
    {
      key: "version",
      header: "Version",
      render: (policy) => <Mono>v{formatNumber(policy.version)}</Mono>,
    },
    {
      key: "status",
      header: "Status",
      render: (policy) => <ToneBadge tone={policyStatusTone(policy)}>{policy.status}</ToneBadge>,
    },
    {
      key: "intent",
      header: "Change intent",
      render: (policy) => <ToneBadge tone={intentTone(policy.intent)}>{policy.intent}</ToneBadge>,
    },
    {
      key: "frozen",
      header: "Frozen cases",
      render: (policy) => (
        <Mono>
          {formatNumber(policy.frozenCasesPassing)} / {formatNumber(policy.frozenCaseCount)}
        </Mono>
      ),
    },
    {
      key: "activated",
      header: "Activated",
      render: (policy) =>
        policy.activatedAt ? (
          <Mono>{formatRelativeTime(policy.activatedAt)}</Mono>
        ) : (
          <span className="text-muted-foreground">Never</span>
        ),
    },
  ];
}

export function PoliciesPage({ onNavigate }: VerificationPageProps) {
  return (
    <MaybeDemo>
      <PoliciesPageInner onNavigate={onNavigate} />
    </MaybeDemo>
  );
}

function PoliciesPageInner({ onNavigate }: VerificationPageProps) {
  const env = useVerificationEnvOptional();
  const environment: VerificationEnvironment | null = env?.environment ?? null;
  const ready = env?.ready ?? true;
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [controlId, setControlId] = useState("all");
  const policiesApi = useApi(
    verificationCanonicalClients.policiesList.operation,
    environment && ready
      ? {
          query: {
            env: environment,
            status: status === "all" ? undefined : (status as VerificationPolicyDto["status"]),
            controlId: controlId === "all" ? undefined : controlId,
          },
        }
      : null,
  );
  const catalog = useMemo(() => {
    return policiesApi.data?.items ?? [];
  }, [policiesApi.data]);
  const controlOptions = useMemo(
    () =>
      [...new Set(catalog.map((policy) => policy.controlId))]
        .map((id) => ({ id, control: policyControl(id) }))
        .sort((a, b) => (a.control?.ref ?? a.id).localeCompare(b.control?.ref ?? b.id)),
    [catalog],
  );
  const rows = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return catalog.filter((policy) => {
      const control = policyControl(policy.controlId);
      if (!needle) return true;
      return (
        policy.id.toLowerCase().includes(needle) ||
        policy.controlId.toLowerCase().includes(needle) ||
        policy.status.toLowerCase().includes(needle) ||
        (control?.title ?? "").toLowerCase().includes(needle) ||
        (control?.ref ?? "").toLowerCase().includes(needle)
      );
    });
  }, [catalog, controlId, environment, q, status]);
  const blocked = catalog.filter((policy) => policy.status === "draft" && policy.blockedReason);

  if (env && !ready) {
    return (
      <PageFrame page="policies" wide eyebrow="Define" title="Policies" lede={POLICIES_LEDE}>
        <PagePending title="Loading environment">
          Loading policy versions for the active Environment.
        </PagePending>
      </PageFrame>
    );
  }
  if (environment && policiesApi.isLoading && !policiesApi.data) {
    return (
      <PageFrame page="policies" wide eyebrow="Define" title="Policies" lede={POLICIES_LEDE}>
        <PagePending title="Loading policies">Fetching policies for this environment.</PagePending>
      </PageFrame>
    );
  }
  if (environment && policiesApi.error && !policiesApi.data) {
    return (
      <PageFrame page="policies" wide eyebrow="Define" title="Policies" lede={POLICIES_LEDE}>
        <PageError
          title="Policies are unavailable"
          error={policiesApi.error}
          onRetry={() => void policiesApi.mutate()}
        />
        <ButtonRow>
          <ActionButton
            actionId={POLICIES_OPEN_ROW_ACTION}
            data-action-id={POLICIES_OPEN_ROW_ACTION}
            disabled
          >
            Open policy
          </ActionButton>
        </ButtonRow>
      </PageFrame>
    );
  }
  if (environment !== "demo" && environment !== "real") {
    return (
      <PageFrame page="policies" wide eyebrow="Define" title="Policies" lede={POLICIES_LEDE}>
        <PagePending title="Environment unavailable">
          Policies are environment-scoped. Pick an environment to inspect its policy versions.
        </PagePending>
      </PageFrame>
    );
  }
  if (catalog.length === 0) {
    return (
      <PageFrame page="policies" wide eyebrow="Define" title="Policies" lede={POLICIES_LEDE}>
        <PagePending title="No policies versioned">
          Policy versions land here. Loosening a policy is a second-person action.{" "}
          <PageLink onNavigate={onNavigate} to="controls">
            Open controls
          </PageLink>
          .
        </PagePending>
      </PageFrame>
    );
  }

  return (
    <PageFrame page="policies" wide eyebrow="Define" title="Policies" lede={POLICIES_LEDE}>
      {blocked.length > 0 ? (
        <Callout tone="unknown">
          {formatNumber(blocked.length)} draft cannot activate. A loosening change that flips a
          frozen case needs a workspace owner who is not the author.
        </Callout>
      ) : null}
      <DataTable
        toolbar={
          <FilterToolbar>
            <FilterField label="Search" wide>
              <ListSearch
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search policies…"
                aria-label="Search policies"
                data-action-id={POLICIES_SEARCH_ACTION}
              />
            </FilterField>
            <FilterField label="Status">
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger
                  className="w-full"
                  data-action-id={POLICIES_FILTER_STATUS_ACTION}
                  aria-label="Filter by policy status"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="retired">Retired</SelectItem>
                </SelectContent>
              </Select>
            </FilterField>
            <FilterField label="Control">
              <Select value={controlId} onValueChange={setControlId}>
                <SelectTrigger
                  className="w-full"
                  data-action-id={POLICIES_FILTER_CONTROL_ACTION}
                  aria-label="Filter by control"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All controls</SelectItem>
                  {controlOptions.map(({ id, control }) => (
                    <SelectItem key={id} value={id}>
                      {control?.ref ?? stripEnvironmentPrefix(id)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FilterField>
          </FilterToolbar>
        }
        emptyState={{
          title: "No policies match",
          description: "Clear search to see every versioned policy.",
        }}
        rows={rows}
        rowKey={(policy) => policy.id}
        rowActionId={POLICIES_OPEN_ROW_ACTION}
        onRowClick={(policy) => onNavigate(href("policies", policy.id))}
        columns={policiesColumns(environment)}
      />
    </PageFrame>
  );
}

export function PolicyDetailPage({ id, onNavigate }: VerificationDetailPageProps) {
  return (
    <MaybeDemo>
      <PolicyDetailPageInner id={id} onNavigate={onNavigate} />
    </MaybeDemo>
  );
}

function PolicyDetailPageInner({ id, onNavigate }: VerificationDetailPageProps) {
  const env = useVerificationEnvOptional();
  const environment: VerificationEnvironment | null = env?.environment ?? null;
  const ready = env?.ready ?? true;
  const [rationale, setRationale] = useState("");
  const [notice, setNotice] = useState<string | null>(null);
  const [pending, setPending] = useState<string | null>(null);
  const policyApi = useApi(
    verificationCanonicalClients.policiesGet.operation,
    environment && ready ? { params: { id }, query: { env: environment } } : null,
  );
  const versionsApi = useApi(
    verificationCanonicalClients.policyVersions.operation,
    environment && ready ? { params: { id }, query: { env: environment } } : null,
  );
  const policy = policyApi.data?.policy ?? null;
  const control = policy ? policyControl(policy.controlId) : undefined;
  const versionRows = useMemo(() => {
    const list = versionsApi.data?.items ?? [];
    return [...list].sort((a, b) => b.version - a.version);
  }, [versionsApi.data]);

  async function onRequestActivation() {
    if (!policy || !rationale.trim()) return;
    setPending(POLICIES_REQUEST_ACTIVATION_ACTION);
    setNotice(null);
    try {
      if (!environment) return;
      const activated = await apiCall(verificationCanonicalClients.policiesActivate.operation, {
        params: { id: policy.id },
        query: { env: environment },
        body: { actorId: environmentActor(environment), rationale: rationale.trim() },
      });
      setNotice(
        `Activated ${stripEnvironmentPrefix(activated.policy.id)} (HG11 ${activated.capability}).`,
      );
      setRationale("");
    } catch (err) {
      setNotice(errorMessage(err));
    } finally {
      setPending(null);
    }
  }

  if (env && !ready) {
    return (
      <PageFrame page="policy" wide eyebrow="Define" title="Policy" lede={POLICY_DETAIL_LEDE}>
        <PagePending title="Loading environment">
          Loading this policy for the active Environment.
        </PagePending>
      </PageFrame>
    );
  }
  if (environment && policyApi.isLoading && !policyApi.data) {
    return (
      <PageFrame page="policy" wide eyebrow="Define" title="Policy" lede={POLICY_DETAIL_LEDE}>
        <PagePending title="Loading policy">Fetching the policy contract.</PagePending>
      </PageFrame>
    );
  }
  if (environment && policyApi.error && !policyApi.data) {
    return (
      <PageFrame page="policy" wide eyebrow="Define" title="Policy" lede={POLICY_DETAIL_LEDE}>
        <PageError
          title={`No policy loaded for ${id}`}
          error={policyApi.error}
          onRetry={() => void policyApi.mutate()}
        />
        <ButtonRow>
          <ActionButton
            actionId={POLICIES_BACK_ACTION}
            data-action-id={POLICIES_BACK_ACTION}
            onClick={() => onNavigate(href("policies"))}
          >
            Back to policies
          </ActionButton>
        </ButtonRow>
      </PageFrame>
    );
  }
  if (environment !== "demo" && environment !== "real") {
    return (
      <PageFrame page="policy" wide eyebrow="Define" title="Policy" lede={POLICY_DETAIL_LEDE}>
        <PagePending title={`No policy loaded for ${id}`}>
          Policy detail is the version, the checks it owns, and the impact of changing it.{" "}
          <PageLink onNavigate={onNavigate} to="policies">
            Back to policies
          </PageLink>
          .
        </PagePending>
      </PageFrame>
    );
  }
  if (!policy) {
    return (
      <PageFrame page="policy" wide eyebrow="Define" title="Policy" lede={POLICY_DETAIL_LEDE}>
        <PagePending title={`No policy loaded for ${id}`}>
          Policy detail is the version, the checks it owns, and the impact of changing it.{" "}
        </PagePending>
        <ButtonRow>
          <ActionButton
            actionId={POLICIES_BACK_ACTION}
            data-action-id={POLICIES_BACK_ACTION}
            onClick={() => onNavigate(href("policies"))}
          >
            Back to policies
          </ActionButton>
        </ButtonRow>
      </PageFrame>
    );
  }

  const canRequest = policy.status === "draft";
  const requestDisabledReason = !canRequest
    ? "Only a draft can be sent for activation."
    : !rationale.trim()
      ? "Rationale is required."
      : null;
  const activeVersion = versionRows.find((row) => row.status === "active") ?? null;
  const replayReady =
    policy.frozenCaseCount > 0 && policy.frozenCasesPassing === policy.frozenCaseCount;
  const deploymentBlockers = [
    policy.blockedReason,
    policy.status !== "draft" ? "deployment-record-already-final" : null,
    policy.intent === "loosen" && !replayReady ? "loosening-policy-replay-required" : null,
  ].filter((item): item is string => Boolean(item));

  return (
    <PageFrame
      page="policy"
      wide
      eyebrow={`Policy · ${control?.ref ?? stripEnvironmentPrefix(policy.controlId)} · version ${formatNumber(policy.version)}`}
      title={control?.title ?? stripEnvironmentPrefix(policy.id)}
      lede={
        policy.status === "active"
          ? `Active since ${policy.activatedAt ? formatRelativeTime(policy.activatedAt) : "unknown"}, activated by ${actorName(policy.activatedByActorId)}.`
          : `Draft created ${formatRelativeTime(policy.createdAt)} by ${actorName(policy.createdByActorId)}.`
      }
      actions={
        <>
          <ToneBadge tone={policyStatusTone(policy)}>{policy.status}</ToneBadge>
          <ToneBadge tone={intentTone(policy.intent)}>{policy.intent}</ToneBadge>
          <ActionButton
            actionId={POLICIES_BACK_ACTION}
            data-action-id={POLICIES_BACK_ACTION}
            onClick={() => onNavigate(href("policies"))}
          >
            Back to policies
          </ActionButton>
        </>
      }
    >
      {notice ? <Callout tone="neutral">{notice}</Callout> : null}
      {versionsApi.error ? (
        <PageError
          title="Policy versions are unavailable"
          error={versionsApi.error}
          onRetry={() => void versionsApi.mutate()}
          actionId="policies.versions-retry"
        />
      ) : null}
      {policy.blockedReason ? (
        <Callout tone="fail">
          This draft cannot be activated silently. {policy.blockedReason} Your role cannot clear
          this — activating a loosening change needs `policy:approve` (HG11) from a workspace owner
          who is not the author.
        </Callout>
      ) : null}
      <div className="flex flex-col gap-4">
        <StatGrid cols={3}>
          <Stat label="Policy version" value={`v${formatNumber(policy.version)}`} />
          <Stat
            label="Active version"
            value={activeVersion ? `v${formatNumber(activeVersion.version)}` : "None"}
          />
          <Stat
            label="Replay cases"
            value={`${formatNumber(policy.frozenCasesPassing)} / ${formatNumber(policy.frozenCaseCount)}`}
          />
        </StatGrid>
        <Card>
          <CardContent className="flex flex-col gap-2">
            <div className="type-body font-medium">Policy expression</div>
            <pre className="overflow-auto type-subtext">{policy.expression}</pre>
          </CardContent>
        </Card>
        <Card>
          <CardContent>
            <div className="type-body font-medium">Replay against frozen cases</div>
            <p className="mt-1 type-subtext text-muted-foreground">
              {replayReady
                ? "Every frozen case still passes this version."
                : "This version is not replay-clean against its frozen cases."}
            </p>
            <p className="mt-2 type-subtext text-muted-foreground">
              {formatNumber(policy.frozenCasesPassing)} of {formatNumber(policy.frozenCaseCount)}{" "}
              passing.
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="flex flex-col gap-3">
            <div className="type-body font-medium">Version history</div>
            {versionRows.length === 0 ? (
              <p className="type-subtext text-muted-foreground">
                No peer versions were returned for this policy.
              </p>
            ) : (
              <div className="flex flex-col gap-2">
                {versionRows.map((row) => (
                  <ActionButton
                    key={row.id}
                    actionId={POLICIES_OPEN_VERSION_ACTION}
                    variant="ghost"
                    className="rounded-md border bg-background p-3 text-left transition hover:bg-muted/40"
                    onClick={() => onNavigate(href("policies", row.id))}
                  >
                    <ListRow
                      title={`Version ${formatNumber(row.version)} · ${row.status}`}
                      subtitle={
                        <>
                          {row.intent} · {formatNumber(row.frozenCasesPassing)} /{" "}
                          {formatNumber(row.frozenCaseCount)} replay cases passing
                        </>
                      }
                      aside={<ToneBadge tone={policyStatusTone(row)}>{row.status}</ToneBadge>}
                    />
                  </ActionButton>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardContent className="flex flex-col gap-3">
            <div className="type-body font-medium">Policy record</div>
            <p className="type-subtext text-muted-foreground">
              <Mono>{stripEnvironmentPrefix(policy.id)}</Mono> · evaluation{" "}
              <Mono>{policy.evaluationMode}</Mono>
            </p>
            <ActionButton
              actionId={POLICIES_OPEN_CONTROL_ACTION}
              data-action-id={POLICIES_OPEN_CONTROL_ACTION}
              onClick={() => onNavigate(href("controls", policy.controlId))}
            >
              Open control {control?.ref ?? stripEnvironmentPrefix(policy.controlId)}
            </ActionButton>
          </CardContent>
        </Card>
        {canRequest ? (
          <Card>
            <CardContent className="flex flex-col gap-3">
              <div className="type-body font-medium">Deployment readiness</div>
              {deploymentBlockers.length > 0 ? (
                <Callout tone="unknown">
                  Deployment is blocked by <Mono>{deploymentBlockers.join(", ")}</Mono>.
                </Callout>
              ) : (
                <Callout tone="pass">Deployment can be sent to a second approver.</Callout>
              )}
              <div className="type-body font-medium">Request activation</div>
              <p className="type-subtext text-muted-foreground">
                Routed to a workspace owner who can `policy:approve`. The request posts through the
                same environment-scoped policiesActivate contract for Demo and Real.
              </p>
              <FilterField label="Why should this change be made?">
                <Textarea
                  value={rationale}
                  onChange={(event) => setRationale(event.target.value)}
                  aria-label="Activation rationale"
                  placeholder="What is wrong with the current version, and why is this the right correction?"
                  data-action-id={POLICIES_RATIONALE_ACTION}
                  rows={4}
                />
              </FilterField>
              <ButtonRow>
                <ActionButton
                  actionId={POLICIES_REQUEST_ACTIVATION_ACTION}
                  data-action-id={POLICIES_REQUEST_ACTIVATION_ACTION}
                  variant="primary"
                  disabled={
                    Boolean(requestDisabledReason) || pending === POLICIES_REQUEST_ACTIVATION_ACTION
                  }
                  reason={requestDisabledReason ?? undefined}
                  onClick={
                    requestDisabledReason || pending === POLICIES_REQUEST_ACTIVATION_ACTION
                      ? undefined
                      : () => void onRequestActivation()
                  }
                >
                  Send for activation
                </ActionButton>
              </ButtonRow>
            </CardContent>
          </Card>
        ) : null}
      </div>
    </PageFrame>
  );
}
